import { apiFetch } from './apiFetch';

export class TenantAdminApiError extends Error {
  status: number;
  body: string;

  constructor(message: string, opts: { status: number; body: string }) {
    super(message);
    this.status = opts.status;
    this.body = opts.body;
  }
}

async function handle<T>(res: Response): Promise<T> {
  if (res.status === 204) {
    return null as T;
  }
  if (!res.ok) {
    const body = await res.text();
    throw new TenantAdminApiError(`Request failed: ${res.status}`, { status: res.status, body });
  }
  return (await res.json()) as T;
}

export type TenantMemberDto = {
  userId: string;
  email: string;
  name: string | null;
  membershipRole: string;
  membershipStatus: string;
  invitedAt: string | null;
  inviteExpiresAt: string | null;
  appRole: string | null;
  customRoleVersionId: string | null;
  customRole: { id: string; name: string; templateRole: string; versionNumber: number } | null;
  canViewPii: boolean;
  canViewHighlySensitive: boolean;
  scopeType: string | null;
  scopeRootNodeId: string | null;
  permissionGrants: Array<{ permission: string; expiresAt: string | null }>;
  activeSessionCount: number;
  lastActiveAt: string | null;
};

export async function listTenantMembers(): Promise<TenantMemberDto[]> {
  const res = await apiFetch('/v1/tenant/members', { cache: 'no-cache' });
  return handle<TenantMemberDto[]>(res);
}

export async function inviteTenantMember(payload: { email: string; name?: string | null; appRole?: string | null }): Promise<{
  userId: string;
  email: string;
  name: string | null;
  membershipRole: string;
  membershipStatus: string;
}> {
  const res = await apiFetch('/v1/tenant/members/invite', {
    method: 'POST',
    json: {
      email: payload.email,
      name: payload.name ? String(payload.name).trim() : null,
      appRole: payload.appRole ? String(payload.appRole).trim() : null,
    },
  });
  return handle(res);
}

export async function resendTenantMemberInvite(payload: { targetUserId: string }): Promise<{ userId: string; inviteExpiresAt: string }> {
  const res = await apiFetch('/v1/tenant/members/invite/resend', {
    method: 'POST',
    json: { targetUserId: payload.targetUserId },
  });
  return handle(res);
}

export async function extendTenantMemberInvite(payload: {
  targetUserId: string;
  inviteExpiresAt?: string | null;
}): Promise<{ userId: string; inviteExpiresAt: string }> {
  const res = await apiFetch('/v1/tenant/members/invite/extend', {
    method: 'POST',
    json: {
      targetUserId: payload.targetUserId,
      inviteExpiresAt: payload.inviteExpiresAt ? String(payload.inviteExpiresAt).trim() : null,
    },
  });
  return handle(res);
}

export async function cancelTenantMemberInvite(payload: {
  targetUserId: string;
}): Promise<{ userId: string; membershipStatus: string }> {
  const res = await apiFetch('/v1/tenant/members/invite/cancel', {
    method: 'POST',
    json: { targetUserId: payload.targetUserId },
  });
  return handle(res);
}

export async function setTenantMemberRole(payload: {
  targetUserId: string;
  role?: string | null;
  customRoleVersionId?: string | null;
  canViewPii?: boolean;
  canViewHighlySensitive?: boolean;
}): Promise<{
  userId: string;
  role: string | null;
  customRoleVersionId: string | null;
  canViewPii: boolean;
  canViewHighlySensitive: boolean;
}> {
  const res = await apiFetch('/v1/tenant/members/role', {
    method: 'POST',
    json: {
      targetUserId: payload.targetUserId,
      role: payload.role ?? null,
      customRoleVersionId: payload.customRoleVersionId ?? null,
      canViewPii: payload.canViewPii,
      canViewHighlySensitive: payload.canViewHighlySensitive,
    },
  });
  return handle(res);
}

export type PermissionCatalogEntryDto = {
  permission: string;
  category: string;
  description: string;
  sensitive: boolean;
};

export async function getTenantPermissionCatalog(): Promise<PermissionCatalogEntryDto[]> {
  const res = await apiFetch('/v1/tenant/roles/permissions/catalog', { cache: 'no-cache' });
  return handle(res);
}

export type TenantCustomRoleListItemDto = {
  id: string;
  name: string;
  description: string | null;
  templateRole: string;
  status: 'ACTIVE' | 'ARCHIVED';
  draft: { id: string; updatedAt: string; permissionsCount: number } | null;
  latestPublished: { id: string; versionNumber: number; publishedAt: string; permissionsCount: number } | null;
  assignedUsersCount: number;
  createdAt: string;
  updatedAt: string;
};

export async function listTenantCustomRoles(): Promise<TenantCustomRoleListItemDto[]> {
  const res = await apiFetch('/v1/tenant/roles/custom', { cache: 'no-cache' });
  return handle(res);
}

export async function createTenantCustomRole(payload: {
  name: string;
  description?: string | null;
  templateRole: string;
}): Promise<{ id: string }> {
  const res = await apiFetch('/v1/tenant/roles/custom', {
    method: 'POST',
    json: {
      name: payload.name,
      description: payload.description ? String(payload.description).trim() : null,
      templateRole: payload.templateRole,
    },
  });
  return handle(res);
}

export async function updateTenantCustomRoleDraft(payload: {
  roleId: string;
  permissions: string[];
}): Promise<{ roleId: string; draftVersionId: string; permissionsCount: number }> {
  const res = await apiFetch(`/v1/tenant/roles/custom/${encodeURIComponent(payload.roleId)}/draft`, {
    method: 'PATCH',
    json: {
      permissions: payload.permissions,
    },
  });
  return handle(res);
}

export async function getTenantCustomRoleDraft(payload: {
  roleId: string;
}): Promise<{ roleId: string; draftVersionId: string; permissions: string[] }> {
  const res = await apiFetch(`/v1/tenant/roles/custom/${encodeURIComponent(payload.roleId)}/draft`, { cache: 'no-cache' });
  return handle(res);
}

export async function publishTenantCustomRole(payload: {
  roleId: string;
}): Promise<{ roleId: string; publishedVersionId: string; versionNumber: number }> {
  const res = await apiFetch(`/v1/tenant/roles/custom/${encodeURIComponent(payload.roleId)}/publish`, {
    method: 'POST',
    json: {},
  });
  return handle(res);
}

export async function assignTenantCustomRoleUsers(payload: {
  roleId: string;
  userIds: string[];
  customRoleVersionId?: string | null;
}): Promise<{ roleId: string; customRoleVersionId: string; assignedUsers: number }> {
  const res = await apiFetch(`/v1/tenant/roles/custom/${encodeURIComponent(payload.roleId)}/assign`, {
    method: 'POST',
    json: {
      userIds: payload.userIds,
      customRoleVersionId: payload.customRoleVersionId ?? null,
    },
  });
  return handle(res);
}

export async function archiveTenantCustomRole(payload: {
  roleId: string;
}): Promise<{ roleId: string; status: string }> {
  const res = await apiFetch(`/v1/tenant/roles/custom/${encodeURIComponent(payload.roleId)}/archive`, {
    method: 'POST',
    json: {},
  });
  return handle(res);
}

export async function duplicateTenantCustomRole(payload: {
  roleId: string;
  name?: string | null;
}): Promise<{ id: string }> {
  const res = await apiFetch(`/v1/tenant/roles/custom/${encodeURIComponent(payload.roleId)}/duplicate`, {
    method: 'POST',
    json: {
      name: payload.name ? String(payload.name).trim() : null,
    },
  });
  return handle(res);
}

export async function setTenantMemberScope(payload: {
  targetUserId: string;
  scopeType: string;
  scopeRootNodeId?: string | null;
}): Promise<{ userId: string; scopeType: string; scopeRootNodeId: string | null }> {
  const res = await apiFetch('/v1/tenant/members/scope', {
    method: 'POST',
    json: {
      targetUserId: payload.targetUserId,
      scopeType: payload.scopeType,
      scopeRootNodeId: payload.scopeRootNodeId ? String(payload.scopeRootNodeId).trim() : null,
    },
  });
  return handle(res);
}

export async function grantTenantMemberPermission(payload: {
  targetUserId: string;
  permission: string;
  expiresAt?: string | null;
  reason?: string | null;
}): Promise<{ userId: string; permission: string; expiresAt: string | null }> {
  const res = await apiFetch('/v1/tenant/members/permissions/grant', {
    method: 'POST',
    json: {
      targetUserId: payload.targetUserId,
      permission: payload.permission,
      expiresAt: payload.expiresAt ? String(payload.expiresAt).trim() : null,
      reason: payload.reason ? String(payload.reason).trim() : null,
    },
  });
  return handle(res);
}

export async function revokeTenantMemberPermission(payload: {
  targetUserId: string;
  permission: string;
}): Promise<{ userId: string; permission: string; revoked: boolean }> {
  const res = await apiFetch('/v1/tenant/members/permissions/revoke', {
    method: 'POST',
    json: {
      targetUserId: payload.targetUserId,
      permission: payload.permission,
    },
  });
  return handle(res);
}

export type TenantPermissionGrantDto = {
  id: string;
  userId: string;
  userEmail: string;
  userName: string | null;
  permission: string;
  expiresAt: string | null;
  createdAt: string;
  reason: string | null;
  grantedByUserId: string | null;
  grantedByEmail: string | null;
  grantedByName: string | null;
};

export async function listTenantPermissionGrants(): Promise<TenantPermissionGrantDto[]> {
  const res = await apiFetch('/v1/tenant/permissions/grants', { cache: 'no-cache' });
  return handle<TenantPermissionGrantDto[]>(res);
}

export async function setTenantMembershipRole(payload: {
  targetUserId: string;
  role: string;
}): Promise<{ userId: string; role: string }> {
  const res = await apiFetch('/v1/tenant/members/membership-role', {
    method: 'POST',
    json: { targetUserId: payload.targetUserId, role: payload.role },
  });
  return handle(res);
}

export async function setTenantMembershipStatus(payload: {
  targetUserId: string;
  status: string;
}): Promise<{ userId: string; status: string; revokedSessions: number }> {
  const res = await apiFetch('/v1/tenant/members/membership-status', {
    method: 'POST',
    json: { targetUserId: payload.targetUserId, status: payload.status },
  });
  return handle(res);
}

export async function revokeAllTenantSessions(): Promise<{ revokedSessions: number }> {
  const res = await apiFetch('/v1/tenant/sessions/revoke-all', { method: 'POST' });
  return handle(res);
}

export async function revokeTenantMemberSessions(payload: {
  targetUserId: string;
}): Promise<{ userId: string; revokedSessions: number }> {
  const res = await apiFetch('/v1/tenant/members/sessions/revoke', {
    method: 'POST',
    json: { targetUserId: payload.targetUserId },
  });
  return handle(res);
}
